import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { COPYRIGHT_LINE } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BetaEnrollDialog } from "@/components/BetaEnrollDialog";
import { OtpAuthPanel } from "@/components/OtpAuthPanel";
import { useForceLight } from "@/context/ThemeContext";

export default function Login() {
  useForceLight();
  const { login, applyUser } = useAuth();
  const [view, setView] = useState("password");
  const [form, setForm] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [betaOpen, setBetaOpen] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await login(form.email, form.password);
      if (data?.requires_2fa) setChallenge(data.challenge);
      // On success the /login route redirects to /dashboard once `user` is set — no manual navigate,
      // which would otherwise override a route the user clicked while options were loading.
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/2fa/verify", { challenge, code });
      await applyUser(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FDFDFD]">
      <div className="hidden lg:flex flex-col justify-between bg-[#1E293B] p-12">
        <Link to="/"><Logo dark /></Link>
        <div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-white leading-tight">
            The daily operating system<br />for your haematology lab.
          </h1>
          <p className="mt-5 text-sm text-slate-400 max-w-md">
            Samples, TAT, critical alerts, rejections, repeats, sub-store, personnel, QC, SOPs,
            equipment and proficiency testing — one clean, customisable record system with
            Excel export on every module.
          </p>
        </div>
        <p className="text-xs text-slate-500 uppercase tracking-[0.18em]">
          LabTrack&trade; by PathLete&reg;
        </p>
        <div className="text-xs text-slate-500 mt-2" data-testid="app-footer">
          <p>Proudly developed in India by PathLete Technologies</p>
          <p className="mt-1" data-testid="app-copyright">{COPYRIGHT_LINE}</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Link to="/"><Logo /></Link></div>

          {view !== "password" ? (
            <OtpAuthPanel mode={view} onBack={() => setView("password")} />
          ) : !challenge ? (
            <>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-[#111827]">Sign in</h2>
              <p className="text-sm text-[#6B7280] mt-1 mb-6">Use the credentials issued to your laboratory.</p>

              <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Email</Label>
                  <Input type="email" value={form.email} onChange={set("email")} required data-testid="email-input" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.1em] text-[#6B7280]">Password</Label>
                  <Input type="password" value={form.password} onChange={set("password")} required data-testid="password-input" className="mt-1.5" />
                </div>
                {error && <p className="text-sm text-[#C92A2A]" data-testid="auth-error">{error}</p>}
                <Button type="submit" disabled={busy} data-testid="submit-btn" className="w-full bg-[#002FA7] hover:bg-[#00237C] text-white">
                  {busy ? "Please wait…" : "Sign in"}
                </Button>
              </form>

              <div className="mt-4 flex items-center justify-between text-sm">
                <button type="button" onClick={() => setView("phone")} data-testid="login-phone-btn"
                  className="text-[#002FA7] hover:underline">Sign in with contact number</button>
                <button type="button" onClick={() => setView("reset")} data-testid="login-forgot-btn"
                  className="text-[#002FA7] hover:underline">Forgot password?</button>
              </div>

              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-[#E5E7EB]" />
                <span className="text-xs text-[#6B7280] uppercase tracking-[0.1em]">or</span>
                <div className="h-px flex-1 bg-[#E5E7EB]" />
              </div>

              <Button variant="outline" onClick={googleLogin} data-testid="google-login-btn" className="w-full">
                Continue with Google
              </Button>

              <div className="mt-6 rounded-md bg-[#F3F4F6] p-3 border border-[#E5E7EB]">
                <p className="text-xs text-[#374151]">
                  Accounts are issued by the developer — self sign-up is closed. Write to{" "}
                  <a href="mailto:info@pathlete.org" className="text-[#002FA7] hover:underline" data-testid="login-contact-link">
                    info@pathlete.org
                  </a>{" "}
                  to onboard your laboratory.
                </p>
                <button type="button" onClick={() => setBetaOpen(true)} data-testid="login-beta-btn"
                  className="text-xs text-[#002FA7] underline underline-offset-2 mt-2">
                  Apply for beta access
                </button>
                <div className="mt-2">
                  <Link to="/admin-login" className="text-xs text-[#002FA7] underline underline-offset-2"
                    data-testid="login-admin-link">Administrator sign in</Link>
                </div>
              </div>
            </>
          ) : (
            <>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-[#111827]">Two-factor check</h2>
              <p className="text-sm text-[#6B7280] mt-1 mb-6">
                Enter the 6-digit code from your authenticator app, or a backup code.
              </p>
              <form onSubmit={verify} className="space-y-4" data-testid="twofa-form">
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456"
                  autoFocus required data-testid="twofa-login-code" />
                {error && <p className="text-sm text-[#C92A2A]" data-testid="auth-error">{error}</p>}
                <Button type="submit" disabled={busy} data-testid="twofa-verify-btn"
                  className="w-full bg-[#002FA7] hover:bg-[#00237C] text-white">
                  {busy ? "Verifying…" : "Verify and sign in"}
                </Button>
                <button type="button" onClick={() => { setChallenge(null); setCode(""); setError(""); }}
                  data-testid="twofa-back-btn" className="text-sm text-[#002FA7] hover:underline">
                  Back to sign in
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      <BetaEnrollDialog open={betaOpen} onClose={() => setBetaOpen(false)} />
    </div>
  );
}
